import { useState, useEffect } from 'react';

interface NavigationProps {
  activeSection?: string;
}

const PHONE_NUMBER = '(804) 362-2555';
const PHONE_HREF = 'tel:+18043622555';
const SMS_HREF = 'sms:+18043622555';
const PROPOSAL_HREF = 'https://api.leadconnectorhq.com/widget/form/0ZsIY0jYLaF8aXm2FeXI';

// Top bar center links — scroll to sections
const TOP_NAV_LINKS = [
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'technology', label: 'Technology' },
  { id: 'what-you-get', label: 'What You Get' },
  { id: 'our-work', label: 'Our Work' },
  { id: 'reviews', label: 'Reviews' },
];

// Side drawer — no duplicates, combined groups
const DRAWER_SECTIONS = [
  { id: 'home', label: 'Home', icon: 'ri-home-4-line' },
  { id: 'enemy', label: 'The Problem', icon: 'ri-error-warning-line' },
  { id: 'snap-system', label: 'SNAP System', icon: 'ri-camera-lens-line' },
  { id: 'calm-guarantee', label: 'Our Guarantee and Honor Code', icon: 'ri-shield-check-line' },
  { id: 'avatars', label: 'Who We Serve', icon: 'ri-group-line' },
  { id: 'billy-owner', label: 'Meet Billy IV and Our Team', icon: 'ri-team-line' },
  { id: 'partner-credibility', label: 'Our Partners', icon: 'ri-handshake-line' },
  { id: 'faq', label: 'FAQ', icon: 'ri-question-answer-line' },
  { id: 'contact', label: 'Contact', icon: 'ri-mail-send-line' },
];

export default function Navigation({ activeSection }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Do NOT lock body scroll — fixes mobile scroll trap
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const scrollToSection = (id: string) => {
    setIsMenuOpen(false);
    // Small delay so drawer closes before scrolling
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 150);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/92 backdrop-blur-md shadow-sm' : 'bg-white'
        }`}
        style={{ height: window.innerWidth < 640 ? '64px' : '72px' }}
      >
        <div className="w-full h-full">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-8 lg:px-12 h-full">
            <div className="flex items-center justify-between h-full gap-4">

              {/* LEFT: Logo */}
              <button
                onClick={() => scrollToSection('home')}
                className="flex items-center cursor-pointer flex-shrink-0 group"
              >
                <img
                  src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/da3f45283dde3421a52ea41a6bce2f15.png"
                  alt="SNAPWindows.ai"
                  onLoad={() => setLogoLoaded(true)}
                  className={`w-auto transition-all duration-700 group-hover:scale-105 ${
                    logoLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                  }`}
                  style={{ height: window.innerWidth < 640 ? '40px' : '56px' }}
                />
              </button>

              {/* CENTER: Top nav section links — desktop only */}
              <div className="hidden xl:flex items-center gap-1 flex-shrink-0">
                {TOP_NAV_LINKS.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className={`px-3 py-2 rounded-lg text-[14px] font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeSection === link.id
                        ? 'text-[#52AB98] bg-[#52AB98]/8'
                        : 'text-[#2B6777] hover:text-[#52AB98] hover:bg-[#52AB98]/6'
                    }`}
                  >
                    {link.label}
                  </button>
                ))}
              </div>

              {/* RIGHT: Pills + hamburger */}
              <div className="flex items-center flex-shrink-0" style={{ gap: '10px' }}>

                {/* Phone pill - desktop only */}
                <a
                  href={PHONE_HREF}
                  className="hidden lg:flex items-center gap-2 px-4 rounded-[14px] border border-[#E4E7EC] text-[#2B6777] hover:bg-[#F9FAFB] transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{ height: '44px', fontSize: '14px', fontWeight: 500 }}
                >
                  <i className="ri-phone-line text-base"></i>
                  <span>{PHONE_NUMBER}</span>
                </a>

                {/* Text Us pill - desktop only */}
                <a
                  href={SMS_HREF}
                  className="hidden lg:flex items-center gap-2 px-4 rounded-[14px] border border-[#E4E7EC] text-[#2B6777] hover:bg-[#F9FAFB] transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{ height: '44px', fontSize: '14px', fontWeight: 500 }}
                >
                  <i className="ri-chat-1-line text-base"></i>
                  <span>Text Us</span>
                </a>

                {/* Primary CTA - desktop only */}
                <a
                  href={PROPOSAL_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden lg:flex items-center gap-2 px-[18px] rounded-[14px] bg-[#52AB98] text-white hover:bg-[#478F82] transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{ height: '44px', fontSize: '15px', fontWeight: 600 }}
                >
                  <span>Get My Proposal</span>
                  <i className="ri-arrow-right-line text-lg"></i>
                </a>

                {/* Hamburger */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="relative rounded-[14px] cursor-pointer transition-all duration-300 bg-[#D98CA3] hover:bg-[#C97B92]"
                  style={{ width: '44px', height: '44px', flexShrink: 0 }}
                  aria-label="Toggle menu"
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
                    <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 ${isMenuOpen ? 'w-5 rotate-45 translate-y-2' : 'w-5'}`} />
                    <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 ${isMenuOpen ? 'w-0 opacity-0' : 'w-4'}`} />
                    <span className={`block h-0.5 bg-white rounded-full transition-all duration-300 ${isMenuOpen ? 'w-5 -rotate-45 -translate-y-2' : 'w-5'}`} />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Slide-over Drawer */}
      <div className={`fixed inset-0 z-40 transition-all duration-500 ${isMenuOpen ? 'visible' : 'invisible pointer-events-none'}`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-[#2B6777]/50 backdrop-blur-sm transition-opacity duration-500 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsMenuOpen(false)}
        />

        {/* Drawer panel */}
        <div className={`absolute top-0 right-0 h-full w-full sm:w-[400px] bg-white shadow-2xl transition-transform duration-500 ease-out flex flex-col ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>

          {/* Drawer Header */}
          <div className="flex items-center justify-between px-6 border-b border-[#E4E7EC] flex-shrink-0" style={{ height: window.innerWidth < 640 ? '64px' : '72px' }}>
            <div>
              <h3 className="text-base font-bold text-[#2B6777]">Explore</h3>
              <p className="text-xs text-[#6B7280]">Jump to any section</p>
            </div>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center hover:bg-[#E5E7EB] transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg text-[#6B7280]"></i>
            </button>
          </div>

          {/* Drawer Content — scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-5">

              {/* Connect block */}
              <div className="mb-5 p-4 bg-[#F8FBFC] rounded-2xl border border-[#E4E7EC]">
                <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-widest mb-3">Get in Touch</p>

                <a
                  href={PROPOSAL_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-[#52AB98] text-white font-semibold rounded-xl hover:bg-[#478F82] transition-all duration-300 cursor-pointer group mb-2"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg">
                      <i className="ri-file-list-3-line text-sm"></i>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold leading-tight whitespace-nowrap">Get My Proposal</p>
                      <p className="text-[11px] text-white/75 font-normal whitespace-nowrap">Free, no obligation</p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-line text-base transition-transform group-hover:translate-x-1"></i>
                </a>

                <div className="flex gap-2">
                  <a
                    href={SMS_HREF}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-[#D98CA3]/40 text-[#D98CA3] font-semibold rounded-xl hover:bg-[#D98CA3]/5 transition-all cursor-pointer text-sm whitespace-nowrap"
                  >
                    <i className="ri-chat-1-line"></i>
                    <span>Text Us</span>
                  </a>
                  <a
                    href={PHONE_HREF}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-[#52AB98]/30 text-[#52AB98] font-semibold rounded-xl hover:bg-[#52AB98]/5 transition-all cursor-pointer text-sm whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    <span>Call Now</span>
                  </a>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-[#E4E7EC]"></div>
                <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Sections</span>
                <div className="h-px flex-1 bg-[#E4E7EC]"></div>
              </div>

              {/* Drawer nav items */}
              <div className="space-y-1">
                {DRAWER_SECTIONS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full px-4 py-3 text-left rounded-xl transition-all duration-200 cursor-pointer flex items-center gap-3 group ${
                      activeSection === item.id
                        ? 'bg-[#52AB98]/10 text-[#52AB98]'
                        : 'text-[#4B5563] hover:bg-[#F9FAFB] hover:text-[#2B6777]'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                      activeSection === item.id
                        ? 'bg-[#52AB98] text-white'
                        : 'bg-[#F3F4F6] text-[#6B7280] group-hover:bg-[#D98CA3]/15 group-hover:text-[#D98CA3]'
                    }`}>
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <span className="flex-1 font-medium text-[14px] leading-snug">{item.label}</span>
                    <i className={`ri-arrow-right-s-line text-lg transition-all duration-200 ${
                      activeSection === item.id ? 'opacity-100 text-[#52AB98]' : 'opacity-0 group-hover:opacity-100'
                    }`}></i>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(16px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </>
  );
}